// ABOUTME: Tiles of the job counters for the job overview, driven by the presentation registry: a
// ABOUTME: tinted tile per counter; a tile with records shows a chevron and opens its drill-down.

import { Box, Card, ChevronRightIcon, Typography, useTheme } from '@ds/coolshop';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { DETAIL_TYPE_LABELS, JobCounterCard } from '../../../../registry';
import { CounterType, DetailType } from '../../../../types';
import { getCounterColorGroup, getCounterStyles } from '../counterStyles';

export type JobSummaryCard = JobCounterCard & { count: number };

const TILE_MIN_WIDTH = 160;

interface IJobSummaryBoxProps {
    cards: JobSummaryCard[];
    /**
     * Orders the tiles by colour and starts a new row at every colour change. Meant for the
     * tile-heavy jobs, where a single flowing grid mixes the colours mid-row.
     */
    groupByColor?: boolean;
    handleToggleDetails: (title: DetailType) => void;
}

/** The tiles in a single row group, or one group per colour. */
function groupCards(cards: JobSummaryCard[], groupByColor?: boolean): JobSummaryCard[][] {
    if (!groupByColor) {
        return [cards];
    }
    const byColor = cards.reduce((acc, card) => {
        const key = getCounterColorGroup(card.counterType);
        acc.set(key, [...(acc.get(key) ?? []), card]);
        return acc;
    }, new Map<number, JobSummaryCard[]>());
    return Array.from(byColor.entries())
        .sort(([a], [b]) => a - b)
        .map(([, groupedCards]) => groupedCards);
}

export function JobSummaryBox(props: Readonly<IJobSummaryBoxProps>) {
    const { cards, groupByColor, handleToggleDetails } = props;
    const theme = useTheme();
    const intl = useIntl();
    const [hovered, setHovered] = useState<DetailType | null>(null);

    const renderTile = (card: JobSummaryCard) => {
        // No errors is nothing to flag: that tile then looks like the neutral ones.
        const counterType =
            card.counterType === CounterType.ERROR && card.count === 0
                ? CounterType.INFO
                : card.counterType;
        const { background, hoverBackground, color } = getCounterStyles(theme, counterType);
        const label = DETAIL_TYPE_LABELS[card.detailType];
        const clickable = !card.disableDetail && card.count > 0;
        return (
            <Card
                key={card.detailType}
                elevation={0}
                height="100%"
                bgcolor={clickable && hovered === card.detailType ? hoverBackground : background}
                cursor={clickable ? 'pointer' : 'default'}
                onClick={clickable ? () => handleToggleDetails(card.detailType) : undefined}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={0.5}
                    p={1.75}
                    height="100%"
                    boxSizing="border-box"
                    onMouseEnter={() => setHovered(card.detailType)}
                    onMouseLeave={() => setHovered(null)}
                >
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="h5" color={color}>
                            <b>{card.count}</b>
                        </Typography>
                        {/* The drill-down cue: only the tiles that open one carry it. */}
                        {clickable && (
                            <Box display="flex" color={color}>
                                <ChevronRightIcon />
                            </Box>
                        )}
                    </Box>
                    <Typography variant="body2" color="text.primary">
                        {label ? intl.formatMessage(label) : card.detailType}
                    </Typography>
                </Box>
            </Card>
        );
    };

    return (
        <Box display="flex" flexDirection="column" gap={1.5}>
            {groupCards(cards, groupByColor).map(group => (
                <Box
                    key={group.map(card => card.detailType).join('|')}
                    display="grid"
                    gridTemplateColumns={`repeat(auto-fill, minmax(${TILE_MIN_WIDTH}px, 1fr))`}
                    gap={1.5}
                >
                    {group.map(renderTile)}
                </Box>
            ))}
        </Box>
    );
}
